import React from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import {
  Button,
  Text,
  theme,
  formatCurrency,
  useResponsive,
  Heading,
  Panel,
  FlexGrid,
  Pill,
} from "@clearabee/ui-library-native";
import { getCalloutCharge, useCatalogue } from "@clearabee/ui-sdk";
import { useQuestionContext } from "lib/providers/questionProvider";
import { FormState } from "./Skips.validation";
import { styles } from "./Skips.styles";

import { Image } from "components/Image/Image";
import placeholderImage from "lib/images/placeholder.png";
import { Anchor } from "components/Core/Anchor/Anchor";
import { useAnalytics } from "lib/hooks/useAnalytics";
import { useRouter } from "lib/hooks/useRouter";

export const PreviousSkip = (): React.ReactElement | null => {
  const [translate] = useTranslation();
  const context = useQuestionContext<FormState>();
  const { getCatalogue, defaultCatalogue } = useCatalogue();
  const { ecommerce } = useAnalytics();
  const { navigate } = useRouter();
  const {
    screens: { medium },
  } = useResponsive();

  const analyticData = {
    item_list_name: `SKIPS Product List`,
    item_list_id: `SKIPS_product_list`,
  };
  const wasteType = context.get("wasteType");
  const refAndPostcode = context.get("refAndPostcode");

  const catalogue =
    getCatalogue(refAndPostcode?.postcode ?? "") || defaultCatalogue;

  const product = catalogue?.items.find(
    (item) => item.sku === refAndPostcode?.previousSkip,
  );

  if (
    (["Soil", "HeavyConstruction"].includes(wasteType ?? "") &&
      parseInt(product?.meta?.yards) > 8) ||
    !product
  ) {
    return null;
  }

  return (
    <View style={styles.previousSkipWrap}>
      <Anchor
        routeName="ProductPage"
        params={{
          categorySku: "SKIPS",
          productSku: product?.sku ?? "",
          postcode: refAndPostcode?.postcode,
          exchangeOrderRef: refAndPostcode?.ref,
          wasteType,
        }}
        onClick={() => {
          ecommerce.selectItem(
            product!,
            analyticData.item_list_name,
            analyticData.item_list_id,
          );
        }}
      >
        <Panel
          shadow={Platform.OS === "web"}
          styles={{
            overflow: "visible",
            paddingVertical: theme.spacing.large,
          }}
        >
          <Pill
            styles={{
              position: "absolute",
              top: -18,
              right: "50%",
              transform: [{ translateX: 84 }],
            }}
            color="usp"
          >
            {translate("hwa.components.skips.currentSkip")}
          </Pill>
          <FlexGrid rowSpacing={null}>
            <FlexGrid.Cell base="30%" medium="20%">
              <Image
                source={product?.img ?? placeholderImage}
                resizeMode="contain"
                style={styles.CTAImage}
              />
            </FlexGrid.Cell>
            <FlexGrid.Cell base="70%" medium="80%">
              <View
                style={[
                  {
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  },
                  medium && {
                    flexDirection: "row",
                    alignItems: "center",
                  },
                ]}
              >
                <View>
                  <Heading fontSize="large" color="brand">
                    {product?.title}
                  </Heading>
                  <Text>{product?.description}</Text>
                  <Text
                    styles={{
                      fontFamily: "ProximaSemiBold",
                      marginTop: theme.spacing.xsmall,
                    }}
                  >
                    {formatCurrency(
                      product?.price ??
                        0 + getCalloutCharge(catalogue.items, product),
                    )}
                    <Text
                      styles={{
                        fontWeight: "100",
                      }}
                    >
                      {" "}
                      {translate("common.incVat")}
                    </Text>
                  </Text>
                </View>
                <Button
                  styles={
                    medium
                      ? { marginLeft: theme.spacing.large }
                      : {
                          marginTop: theme.spacing.small,
                        }
                  }
                  onPress={() => {
                    ecommerce.selectItem(
                      product!,
                      analyticData.item_list_name,
                      analyticData.item_list_id,
                    );
                    navigate({
                      routeName: "ProductPage",
                      params: {
                        categorySku: "SKIPS",
                        productSku: product?.sku ?? "",
                        postcode: refAndPostcode?.postcode,
                        exchangeOrderRef: refAndPostcode?.ref,
                        wasteType,
                      },
                    });
                  }}
                  size="medium"
                  color="warning"
                >
                  {translate("common.bookAgain")}
                </Button>
              </View>
            </FlexGrid.Cell>
          </FlexGrid>
        </Panel>
      </Anchor>
    </View>
  );
};
